import { lazy, Suspense, useEffect } from 'react';
import { Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Toaster } from '@xtreed/academy-shared/ui';
import { Loader2 } from 'lucide-react';
import { usePWAStore } from './stores/pwa';
import { useLanguageSync } from '@/hooks/useLanguage';
import { useAuthStore } from './stores/auth';
import { scheduleTokenRefresh } from './services/api';
import { termsService } from './services/terms.service';

// Layouts (kept eager - they wrap all routes)
import { AuthLayout } from '@/components/layouts/AuthLayout';
import { StudentLayout } from '@/components/layouts/StudentLayout';
import { AdminLayout } from '@/components/layouts/AdminLayout';
import { LessonLayout } from '@/components/layouts/LessonLayout';

// Guards (kept eager - they gate route access)
import { PrivateRoute } from '@/components/guards/PrivateRoute';
import { AdminRoute } from '@/components/guards/AdminRoute';
import { UserRole } from '@xtreed/academy-shared';
import { SurveyModal as SurveyModalGlobal } from '@/components/surveys/SurveyModal';

// Auth Pages (lazy)
const LoginPage = lazy(() =>
  import('@/pages/auth/LoginPage').then((m) => ({ default: m.LoginPage })),
);
const ForgotPasswordPage = lazy(() =>
  import('@/pages/auth/ForgotPasswordPage').then((m) => ({
    default: m.ForgotPasswordPage,
  })),
);
const SetPasswordPage = lazy(() =>
  import('@/pages/auth/SetPasswordPage').then((m) => ({
    default: m.SetPasswordPage,
  })),
);
const ResetPasswordPage = lazy(() =>
  import('@/pages/auth/ResetPasswordPage').then((m) => ({
    default: m.ResetPasswordPage,
  })),
);
const DeepLinkLoginPage = lazy(() =>
  import('@/pages/auth/DeepLinkLoginPage').then((m) => ({
    default: m.DeepLinkLoginPage,
  })),
);
const DevflixAuthorizePage = lazy(() =>
  import('@/pages/auth/DevflixAuthorizePage').then((m) => ({
    default: m.DevflixAuthorizePage,
  })),
);

// Student Pages (lazy)
const HomePage = lazy(() =>
  import('@/pages/student/HomePage').then((m) => ({
    default: m.HomePage,
  })),
);
const CoursesPage = lazy(() =>
  import('@/pages/student/CoursesPageEnhanced').then((m) => ({
    default: m.CoursesPageEnhanced,
  })),
);
const CoursePage = lazy(() =>
  import('@/pages/student/CoursePage').then((m) => ({
    default: m.CoursePage,
  })),
);
const CourseTrailPage = lazy(() =>
  import('@/pages/student/CourseTrailPage').then((m) => ({
    default: m.CourseTrailPage,
  })),
);
const LessonPage = lazy(() =>
  import('@/pages/student/LessonPage').then((m) => ({
    default: m.LessonPage,
  })),
);
const ProfilePage = lazy(() =>
  import('@/pages/student/ProfilePage').then((m) => ({
    default: m.ProfilePage,
  })),
);
const CertificatesPage = lazy(() =>
  import('@/pages/student/CertificatesPage').then((m) => ({
    default: m.CertificatesPage,
  })),
);
const RankingPage = lazy(() =>
  import('@/pages/student/RankingPage').then((m) => ({
    default: m.RankingPage,
  })),
);
const CommunityPage = lazy(() =>
  import('@/pages/student/CommunityPage').then((m) => ({
    default: m.CommunityPage,
  })),
);

// Admin Pages (lazy)
const AdminDashboardPage = lazy(() =>
  import('@/pages/admin/DashboardPage').then((m) => ({
    default: m.AdminDashboardPage,
  })),
);
const AdminCoursesPage = lazy(() =>
  import('@/pages/admin/CoursePage').then((m) => ({
    default: m.AdminCoursesPage,
  })),
);
const AdminCourseEditPage = lazy(() =>
  import('@/pages/admin/CoursePage/CourseEditPage').then((m) => ({
    default: m.CourseEditPage,
  })),
);
const AdminCourseAppearancePage = lazy(() =>
  import('@/pages/admin/CoursePage/AppearancePage').then((m) => ({
    default: m.CourseAppearancePage,
  })),
);
const AdminAccessGroupsPage = lazy(() =>
  import('@/pages/admin/AccessGroupsPage').then((m) => ({
    default: m.AdminAccessGroupsPage,
  })),
);
const AdminAccessGroupDetailPage = lazy(() =>
  import('@/pages/admin/AccessGroupDetailPage').then((m) => ({
    default: m.AdminAccessGroupDetailPage,
  })),
);
const AdminCertificatesPage = lazy(() =>
  import('@/pages/admin/CertificatesPage').then((m) => ({
    default: m.AdminCertificatesPage,
  })),
);
const AdminCertificateEditorPage = lazy(() =>
  import('@/pages/admin/CertificateEditorPage').then((m) => ({
    default: m.AdminCertificateEditorPage,
  })),
);
const CertificateValidationPage = lazy(() =>
  import('@/pages/public/CertificateValidationPage').then((m) => ({
    default: m.CertificateValidationPage,
  })),
);
const AdminUsersPage = lazy(() =>
  import('@/pages/admin/UsersPage').then((m) => ({
    default: m.AdminUsersPage,
  })),
);
const AdminUserEditPage = lazy(() =>
  import('@/pages/admin/UserEditPage').then((m) => ({
    default: m.AdminUserEditPage,
  })),
);
const AdminCmsPage = lazy(() =>
  import('@/pages/admin/CmsPage').then((m) => ({ default: m.AdminCmsPage })),
);
const AdminGamificationPage = lazy(() =>
  import('@/pages/admin/GamificationPage').then((m) => ({
    default: m.AdminGamificationPage,
  })),
);
const AdminSettingsPage = lazy(() =>
  import('@/pages/admin/SettingsPage').then((m) => ({
    default: m.AdminSettingsPage,
  })),
);
const AdminEmailTemplatesPage = lazy(() =>
  import('@/pages/admin/EmailTemplatesPage').then((m) => ({
    default: m.AdminEmailTemplatesPage,
  })),
);
const PlatformPage = lazy(() =>
  import('@/pages/platform/PlatformPage').then((m) => ({
    default: m.PlatformPage,
  })),
);
const AdminRecursosPage = lazy(() =>
  import('@/pages/admin/RecursosPage').then((m) => ({
    default: m.RecursosPage,
  })),
);
const AdminBrandingPage = lazy(() =>
  import('@/pages/admin/BrandingPage').then((m) => ({
    default: m.BrandingPage,
  })),
);
const AdminDomainsPage = lazy(() =>
  import('@/pages/admin/DomainsPage').then((m) => ({
    default: m.DomainsPage,
  })),
);
const AdminVideosPage = lazy(() =>
  import('@/pages/admin/VideosPage').then((m) => ({
    default: m.VideosPage,
  })),
);
const AdminVideoViewersPage = lazy(() =>
  import('@/pages/admin/VideoViewersPage').then((m) => ({
    default: m.VideoViewersPage,
  })),
);
const AdminTenantUsagePage = lazy(() =>
  import('@/pages/admin/TenantUsagePage').then((m) => ({
    default: m.TenantUsagePage,
  })),
);
const AdminVideoStudioPage = lazy(() =>
  import('@/pages/admin/VideoStudioPage').then((m) => ({
    default: m.VideoStudioPage,
  })),
);
const AdminWebhooksPage = lazy(() =>
  import('@/pages/admin/WebhooksPage').then((m) => ({
    default: m.WebhooksPage,
  })),
);
const AdminCustomFieldsPage = lazy(() =>
  import('@/pages/admin/CustomFieldsPage').then((m) => ({
    default: m.AdminCustomFieldsPage,
  })),
);
const AdminSurveysPage = lazy(() =>
  import('@/pages/admin/SurveysPage').then((m) => ({
    default: m.AdminSurveysPage,
  })),
);
const AdminSurveyEditPage = lazy(() =>
  import('@/pages/admin/SurveyEditPage').then((m) => ({
    default: m.AdminSurveyEditPage,
  })),
);
const AdminCategoriesPage = lazy(() =>
  import('@/pages/admin/CategoriesPage').then((m) => ({
    default: m.AdminCategoriesPage,
  })),
);
const AdminPWAPage = lazy(() =>
  import('@/pages/admin/PWAPage').then((m) => ({
    default: m.PWAPage,
  })),
);
const AdminModulesPage = lazy(() =>
  import('@/pages/admin/ModulesPage').then((m) => ({
    default: m.ModulesPage,
  })),
);
const AdminModuleEditPage = lazy(() =>
  import('@/pages/admin/ModulesPage/ModuleEditPage').then((m) => ({
    default: m.ModuleEditPage,
  })),
);
const TermsAcceptancePage = lazy(() =>
  import('@/pages/auth/TermsAcceptancePage').then((m) => ({
    default: m.TermsAcceptancePage,
  })),
);

/** Redirects to /terms-acceptance if user hasn't accepted terms */
function TermsGuard({ children }: { children: React.ReactNode }) {
  const user = useAuthStore((s) => s.user);
  const isSchoolAdmin =
    user?.role === UserRole.ADMIN || user?.role === UserRole.SUPER_ADMIN;
  const mustAccept = !!user && !isSchoolAdmin && !(user as any).termsAcceptedAt;
  const { data: contract, isLoading } = useQuery({
    queryKey: ['terms-contract'],
    queryFn: () => termsService.getContract(),
    enabled: mustAccept,
  });
  if (mustAccept && isLoading) return null;
  if (mustAccept && contract?.url) {
    return <Navigate to="/terms-acceptance" replace />;
  }
  return <>{children}</>;
}

function ScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);
  return null;
}

function PageLoader() {
  return (
    <div className="flex min-h-[60vh] items-center justify-center">
      <Loader2 className="h-8 w-8 animate-spin text-primary-ink" />
    </div>
  );
}

function App() {
  const { initialize } = usePWAStore();
  const { isAuthenticated } = useAuthStore();
  useLanguageSync();

  useEffect(() => {
    // Initialize PWA on app start
    initialize();
  }, [initialize]);

  useEffect(() => {
    // Schedule proactive token refresh for already-logged-in users
    if (isAuthenticated) {
      scheduleTokenRefresh(432000); // 5 days default
    }
  }, [isAuthenticated]);

  return (
    <>
      <ScrollToTop />
      <Suspense fallback={<PageLoader />}>
        <Routes>
          {/* Painel da Xtreed: fora do AuthLayout, porque nao pertence a
              cliente nenhum e nao usa o tema de tenant. */}
          <Route path="/platform" element={<PlatformPage />} />
          <Route
            path="/plataforma"
            element={<Navigate to="/platform" replace />}
          />

          {/* Auth Routes */}
          <Route element={<AuthLayout />}>
            <Route path="/login" element={<LoginPage />} />
            {/* Mesma tela, campo de marca diferente: serve para comparar
                os três loops no ambiente real antes de fechar um. */}
            <Route path="/login/:variant" element={<LoginPage />} />
            <Route path="/forgot-password" element={<ForgotPasswordPage />} />
            <Route path="/reset-password" element={<ResetPasswordPage />} />
            <Route path="/set-password" element={<SetPasswordPage />} />
          </Route>

          {/* Deep Link — standalone (no layout) */}
          <Route path="/auth/deep-link" element={<DeepLinkLoginPage />} />
          <Route
            path="/sso/devflix/authorize"
            element={
              <PrivateRoute>
                <DevflixAuthorizePage />
              </PrivateRoute>
            }
          />

          {/* Validação pública de certificado — sem auth */}
          <Route
            path="/verify-certificate"
            element={<CertificateValidationPage />}
          />
          <Route
            path="/verify-certificate/:code"
            element={<CertificateValidationPage />}
          />

          {/* Terms Acceptance (must be BEFORE TermsGuard routes) */}
          <Route
            path="/terms-acceptance"
            element={
              <PrivateRoute>
                <TermsAcceptancePage />
              </PrivateRoute>
            }
          />

          {/* Lesson Routes - Immersive Layout (no sidebar) */}
          <Route
            element={
              <PrivateRoute>
                <TermsGuard>
                  <LessonLayout />
                </TermsGuard>
              </PrivateRoute>
            }
          >
            <Route path="/lesson/:lessonId" element={<LessonPage />} />
            <Route
              path="/courses/:slug/lessons/:lessonId"
              element={<LessonPage />}
            />
          </Route>

          {/* Student Routes */}
          <Route
            element={
              <PrivateRoute>
                <TermsGuard>
                  <StudentLayout />
                </TermsGuard>
              </PrivateRoute>
            }
          >
            <Route path="/home" element={<HomePage />} />
            <Route path="/courses" element={<CoursesPage />} />
            <Route path="/courses/:slug" element={<CoursePage />} />
            <Route path="/courses/:slug/trail" element={<CourseTrailPage />} />
            <Route path="/ranking" element={<RankingPage />} />
            <Route path="/certificates" element={<CertificatesPage />} />
            <Route path="/profile" element={<ProfilePage />} />
            <Route path="/community" element={<CommunityPage />} />
          </Route>

          {/* Admin Routes */}
          <Route
            path="/admin"
            element={
              <AdminRoute>
                <TermsGuard>
                  <AdminLayout />
                </TermsGuard>
              </AdminRoute>
            }
          >
            <Route index element={<AdminDashboardPage />} />
            <Route path="courses" element={<AdminCoursesPage />} />
            <Route path="courses/:id" element={<AdminCourseEditPage />} />
            <Route path="modules" element={<AdminModulesPage />} />
            <Route path="modules/:id" element={<AdminModuleEditPage />} />
            <Route path="videos" element={<AdminVideosPage />} />
            <Route path="videos/viewers" element={<AdminVideoViewersPage />} />
            <Route path="videos/:id" element={<AdminVideoStudioPage />} />
            <Route path="categories" element={<AdminCategoriesPage />} />
            <Route path="access-groups" element={<AdminAccessGroupsPage />} />
            <Route
              path="access-groups/:id"
              element={<AdminAccessGroupDetailPage />}
            />
            <Route path="certificates" element={<AdminCertificatesPage />} />
            <Route
              path="certificates/:id/editor"
              element={<AdminCertificateEditorPage />}
            />
            <Route path="users" element={<AdminUsersPage />} />
            <Route path="users/:id/edit" element={<AdminUserEditPage />} />
            <Route path="cms" element={<AdminCmsPage />} />
            <Route path="gamification" element={<AdminGamificationPage />} />
            <Route path="settings" element={<AdminSettingsPage />} />
            <Route
              path="email-templates"
              element={<AdminEmailTemplatesPage />}
            />
            <Route path="features" element={<AdminRecursosPage />} />
            <Route path="branding" element={<AdminBrandingPage />} />
            <Route path="domains" element={<AdminDomainsPage />} />
            <Route path="webhooks" element={<AdminWebhooksPage />} />
            <Route path="custom-fields" element={<AdminCustomFieldsPage />} />
            <Route path="surveys" element={<AdminSurveysPage />} />
            <Route path="surveys/:id" element={<AdminSurveyEditPage />} />
            <Route path="video-usage" element={<AdminTenantUsagePage />} />
            <Route path="pwa" element={<AdminPWAPage />} />
          </Route>

          {/* Course appearance editor — standalone (no admin layout), opens in new tab */}
          <Route
            path="/admin/courses/:id/appearance"
            element={
              <AdminRoute>
                <TermsGuard>
                  <AdminCourseAppearancePage />
                </TermsGuard>
              </AdminRoute>
            }
          />

          {/* Redirects */}
          <Route path="/" element={<Navigate to="/home" replace />} />
          <Route path="/dashboard" element={<Navigate to="/home" replace />} />

          <Route path="*" element={<Navigate to="/home" replace />} />
        </Routes>
      </Suspense>

      <Toaster />
      <SurveyModalGlobal />
    </>
  );
}

export default App;
